<script setup lang="ts">
import CloseSmallIcon from '@/assets/icons/shared/close-small.svg';
import UiButton from '@/modules/ui/components/ui/UiButton.vue';

const {
  tag = 'div',
  mode = 'outline',
  color = 'gray',
  shape = 'rectangle',
} = defineProps<{
  tag?: 'div' | 'li';
  mode?: 'outline' | 'fill';
  color?: 'gray' | 'black' | 'yellow';
  shape?: 'rectangle' | 'round';
  clickable?: boolean;
  closable?: boolean;
}>();

defineEmits<{
  click: [event: MouseEvent];
  close: [event: MouseEvent];
}>();
</script>

<template>
  <component
    :is="tag"
    class="ui-chip"
    :class="[`_mode-${mode}`, `_color-${color}`, `_shape-${shape}`, { _clickable: clickable, _closable: closable }]"
    @click="$emit('click', $event)"
  >
    <slot />

    <UiButton
      v-if="closable"
      size="s"
      color="gray"
      mode="plain"
      shape="square"
      @click.stop="$emit('close', $event)"
    >
      <CloseSmallIcon />
    </UiButton>
  </component>
</template>

<style scoped lang="scss">
.ui-chip {
  display: flex;
  align-items: center;

  font-size: var(--font-size);
  line-height: var(--line-height);
  word-break: break-word;

  transition:
    border var(--transition-fast),
    color var(--transition-fast),
    background-color var(--transition-fast);

  &._mode-outline {
    background-color: var(--color-white);

    &._closable {
      padding: 3px 3px 3px 7px;
    }

    &:not(._closable) {
      padding: 3px 7px;
    }

    @mixin outline-color($name, $border, $color, $border-hover, $color-hover) {
      &._color-#{$name} {
        border: $border;
        color: $color;

        &._clickable {
          @include hover {
            border: $border-hover;
            color: $color-hover;
          }
        }
      }
    }

    @include outline-color(
      'gray',
      var(--border-gray-200),
      var(--color-gray-600),
      var(--border-gray-500),
      var(--color-gray-900)
    );
    @include outline-color(
      'black',
      var(--border-gray-200),
      var(--color-gray-1000),
      var(--border-gray-500),
      var(--color-black)
    );
  }

  &._mode-fill {
    font-weight: var(--font-weight-bold);

    &._closable {
      padding: 4px 4px 4px 8px;
    }

    &:not(._closable) {
      padding: 4px 8px;
    }

    @mixin fill-color($name, $background-color, $color, $background-color-hover) {
      &._color-#{$name} {
        background-color: $background-color;
        color: $color;

        &._clickable {
          @include hover {
            background-color: $background-color-hover;
          }
        }
      }
    }

    @include fill-color('gray', var(--color-gray-200), var(--color-gray-800), var(--color-gray-300));
    @include fill-color('black', var(--color-gray-800), var(--color-gray-200), var(--color-gray-900));
    @include fill-color('yellow', var(--color-yellow-600), var(--color-gray-1000), var(--color-yellow-700));
  }

  &._shape-rectangle {
    border-radius: 8px;
  }

  &._shape-round {
    border-radius: 100px;
  }

  &._clickable {
    cursor: pointer;
  }
}

.ui-button {
  margin: -4px -4px -4px -2px;
  flex-shrink: 0;
}
</style>
